import React, { useRef } from 'react';
import Image from 'next/image';
import { Box, Grid, Typography, type Theme } from '@mui/material';
import { ValidatedInput } from 'src/components/screens/shared/validate-input';
import { ScreenContainer } from 'src/components/flow';
import type { FlowScreenProps } from 'src/components/screens/types';
import { useImpressionEvent } from 'src/utils/analytics/hooks';
import { EntityName, EventType } from 'src/utils/analytics/constants';
import {
  PasswordRequirements,
  passwordRegexEval,
} from './password-requirements';
import { CtaTerms } from 'src/components/screens/register';
import { useDispatch } from 'react-redux';
import { getUserEmail } from 'src/redux/selectors';
import { useAppSelector } from 'src/redux/hooks';
import {
  updateSignupAnalytics,
  updateUserEmail,
} from 'src/redux/reducers/user-data';
import { gtmPushToDataLayer } from 'src/utils/analytics/utils';
import { usePassword } from 'src/components/providers/password';
import { Screen } from 'src/lib/flows/screens';
import { Routes } from 'src/lib/constants';
import { useAnalyticsPageView } from '@nerdwallet-cards/analytics';

const REG_HERO_IMAGE =
  'https://www.nerdwallet.com/cdn/js-auth-tools/branded-elements/generic-hero-variant-full.svg';

const EMAIL_FORMAT =
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const Registration: React.FC<FlowScreenProps> = ({
  formImpressionId: parentImpressionID,
  navigationId,
  sectionName,
}) => {
  const inputLabel =
    'Would you like to create a NerdWallet account to save your recommendations?';
  const navigationID = useRef<string | null>(null);
  useImpressionEvent(
    {
      eventType: EventType.RegistrationCtaImpression,
      navigationId,
      immediate: true,
      payload: {
        entity_name: EntityName.Register,
        parent_impression_id: parentImpressionID,
        section_name: EntityName.Register,
        additional_attributes: {
          target_flow: 'cardfinder',
          cta_mode: 'embed',
          auth_type: 'register',
          cta_label: inputLabel,
          cta_format: 'button',
        },
      },
    },
    navigationID.current === navigationId
  );
  navigationID.current = navigationId;

  const dispatch = useDispatch();
  const email = useAppSelector(getUserEmail);
  const isEmailValid = !!email && EMAIL_FORMAT.test(email);

  const { password, setPassword } = usePassword();
  const isPasswordValid = !!password && passwordRegexEval(password);
  const { pageViewId } = useAnalyticsPageView();

  return (
    <ScreenContainer
      primaryCta={{
        text: 'sign up & see my card picks',
        terms: CtaTerms,
        isDisabled: !isPasswordValid || !isEmailValid,
        style: (theme: Theme) => ({
          marginTop: 3,
          width: '343px',
          display: 'flex',
          flexDirection: 'column',
          [theme.breakpoints.up('sm')]: {
            alignItems: 'flex-start',
          },
          [theme.breakpoints.down('sm')]: {
            zIndex: 1,
            padding: 3,
            alignItems: 'center',
            width: '100%',
          },
        }),
        onClick: async () => {
          gtmPushToDataLayer({ event: 'formcomplete_reg' });
          dispatch(
            updateSignupAnalytics({
              pageName: sectionName,
              pageImpressionID: pageViewId,
              pagePath: window.location.href,
            })
          );
        },
        showSignInContent: true,
        skipLink: {
          href: `${Routes.Flow}/${Screen.Loading}`,
          label: 'No, let me see my results',
          onClick: () => {
            gtmPushToDataLayer({ event: 'formcomplete' });
          },
          sxProps: { marginTop: 3 },
        },
      }}
    >
      <Box maxWidth="md">
        <Typography
          variant="titleSmall"
          textAlign={{ xs: 'center', md: 'left' }}
          display={{ xs: 'block' }}
          marginBottom={2}
        >
          Would you like to create a NerdWallet account to save your
          recommendations?
        </Typography>
        <Typography
          variant="textLarge"
          textAlign="left"
          display={{ xs: 'none', md: 'block' }}
        >
          You can also stay on top of your credit cards, track your cashflow and
          get personalized recommendations on how you can save.
        </Typography>
      </Box>
      <Grid
        container
        marginTop={3}
        direction={{ xs: 'column-reverse', md: 'row' }}
        alignItems={{ xs: 'flex-start', md: 'flex-end' }}
      >
        <Grid item md={6} width="100%" marginBottom={5}>
          <Box
            maxWidth={{ sm: '368px' }}
            marginTop={4}
            marginBottom={{ xs: 1, md: 2 }}
          >
            <Typography
              component="label"
              htmlFor="email"
              display="block"
              marginBottom={1}
              variant="textSmall"
            >
              Email
            </Typography>
            <ValidatedInput
              autoFocus
              defaultValue={email}
              errorMessage="Please enter a valid email"
              id="email"
              isValid={isEmailValid}
              onChange={(value) => dispatch(updateUserEmail(value))}
            />
          </Box>
          <Box display="flex" flexDirection="column" gap={2}>
            <Box maxWidth={{ sm: '368px' }} marginTop={3}>
              <Typography
                component="label"
                htmlFor="password"
                display="block"
                marginBottom={1}
                variant="textSmall"
              >
                Password
              </Typography>
              <ValidatedInput
                errorMessage="Must contain 8 characters, one uppercase letter, and one number or special character"
                id="password"
                isValid={isPasswordValid}
                onChange={(value) => setPassword(value)}
                type="password"
              />
            </Box>
            <PasswordRequirements password={password} />
          </Box>
        </Grid>
        <Grid item md={6}>
          <Box
            display={{ xs: 'none', md: 'flex' }}
            sx={(theme) => ({
              [theme.breakpoints.up('md')]: {
                display: 'flex',
                transform: 'translateY(-50px)',
              },
            })}
          >
            <Image
              unoptimized
              src={REG_HERO_IMAGE}
              alt="register hero image"
              width={0}
              height={0}
              sizes="100vw"
              style={{ width: '100%', height: 'auto', maxHeight: '315px' }}
            />
          </Box>
        </Grid>
      </Grid>
    </ScreenContainer>
  );
};

export default Registration;
